import {
  useEnhanceRecordResources,
  useListRecords,
  useRecordMeasurementTypeSpecificTableHeader,
} from "#hooks/adapters/useRecords";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import BulkRecordStatusChangeButton from "#src/batteries-included-components/Buttons/BulkRecordStatusChangeButton/BulkRecordStatusChangeButton";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { RecordValueDrawer } from "#src/batteries-included-components/Drawers/RecordValueDrawer";
import { useRecordValueDrawer } from "#src/batteries-included-components/Drawers/RecordValueDrawer/RecordValueDrawer.helper";
import { RecordsTableFilterArea } from "#src/batteries-included-components/FilterAreas/RecordsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import InlineRecordValueStatusPicker from "#src/components/Common/InlineRecordValueStatusPicker/InlineRecordValueStatusPicker";
import { RecordValueCell } from "#src/components/Common/RecordValueCell/RecordValueCell";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  useApplyDefaultRecordConfiguration,
  useApplyDefaultRecordConfigurationByConfigAsync,
} from "#src/queries/recordQueries";
import { ExceptionUtils } from "#src/utils/exception";
import {
  useDataTableColumnConfigurationProps,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { linkToRecordDetail } from "#utils/links";
import { useMutation } from "@tanstack/react-query";
import {
  Accordion,
  Button,
  CheckboxInput,
  DataTablePanel,
  Dialog,
  Form,
  HeaderType,
  ItemAction,
  StorageKeys,
  useForm,
  useToast,
} from "@validereinc/common-components";
import type { AssetTypeType, RecordType } from "@validereinc/domain";
import { AssetType, RecordDomain, SortDirection } from "@validereinc/domain";
import {
  BaseError,
  datetimeFormatter,
  downloadLink,
  fromUTC,
  getDiscreteYearMonths,
  monthFormatter,
  yearMonthFormatter,
  yearMonthParser,
} from "@validereinc/utilities";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useMemo, useState } from "react";
import { getExportGroupBy } from "./RecordsListTablePanel.helpers";

export const RecordsTablePanel = ({
  type,
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  applySeededAutomationJobStorageKey,
}: {
  type: AssetTypeType;
  applySeededAutomationJobStorageKey: string;
} & Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "viewConfigStorageKey"
  | "tableConfigStorageKey"
  | "tableStateStorageKey"
>) => {
  const { flowId, equipmentId, facilityId } = useParams<{
    flowId: string;
    equipmentId: string;
    facilityId: string;
  }>();
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Equipment Records list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });
  const { getTypeName, isLoading: isMeasurementTypesLoading } =
    useMeasurementTypes();
  const { toast } = useToast();
  const [showApplyWarning, setShowApplyWarning] = useState(false);

  const [viewFilters] = useStickyState<{
    reporting_group_id?: string;
    date_range?: { from: string; to: string };
  }>({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState<{
    measurement_type?: string;
    status?: string[];
  }>({}, filterConfigStorageKey);
  const filters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const {
    reporting_group_id,
    date_range,
    measurement_type: measurement_type_filter,
    status: status_filter,
  } = filters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "year_month",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const [selectedRecords, setSelectedRecords] = useState({});

  const applyAutomation = useApplyDefaultRecordConfiguration();
  const applySeededAutomationAsync =
    useApplyDefaultRecordConfigurationByConfigAsync(
      applySeededAutomationJobStorageKey
    );

  const asset_id = useMemo(() => {
    switch (type) {
      case AssetType.FACILITY:
        return facilityId;
      case AssetType.EQUIPMENT:
        return equipmentId;
      case AssetType.FLOW:
        return flowId;
      default:
        return flowId;
    }
  }, [type, facilityId, equipmentId, flowId]);

  const exportGroupBy = getExportGroupBy(type);
  const { mutate: handleExport, isLoading: isExporting } = useMutation({
    mutationFn: async () => {
      const exportRecordParams: Parameters<typeof RecordDomain.exportList>[0] =
        {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
          groupBy: exportGroupBy,
          filters: {
            asset_id,
            ...(reporting_group_id
              ? { reporting_group_id: { $exact: reporting_group_id } }
              : {}),
            ...(measurement_type_filter
              ? ({
                  measurement_type: { $exact: measurement_type_filter },
                } as any)
              : {}),
            ...(status_filter?.length
              ? ({ status: status_filter } as any)
              : {}),
            ...(date_range
              ? {
                  $and: [
                    {
                      year_month: {
                        $gte: minYearMonth,
                      },
                    },
                    {
                      year_month: {
                        $lte: maxYearMonth,
                      },
                    },
                  ] as any,
                }
              : {}),
          },
        };

      const recordReport = await RecordDomain.exportList(exportRecordParams);

      if (!recordReport?.s3_download_link) {
        throw new BaseError(`Could not fetch download URL for records export`, {
          cause: recordReport,
        });
      }
      downloadLink(
        recordReport.s3_download_link,
        `Records - ${datetimeFormatter(new Date())}`
      );
      toast.push({
        intent: "success",
        description: "Records exported successfully.",
      });
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export records.",
      });
    },
  });

  const minYearMonth = date_range?.from
    ? yearMonthFormatter(fromUTC(parseISO(date_range?.from)))
    : yearMonthFormatter(DEFAULT_DATE_RANGES.lastTwelveMonths.from);
  const maxYearMonth = date_range?.to
    ? yearMonthFormatter(fromUTC(parseISO(date_range?.to)))
    : yearMonthFormatter(DEFAULT_DATE_RANGES.lastTwelveMonths.to);

  const isRecordQueryEnabled = !!reporting_group_id;
  const recordQuery = useListRecords(
    {
      page: tableState.page,
      pageSize: tableState.pageSize,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      groupBy: ["id", "year_month"],
      filters: {
        asset_id,
        reporting_group_id: { $exact: reporting_group_id },
        ...(status_filter?.length ? ({ status: status_filter } as any) : {}),
        $and: [
          {
            year_month: {
              $gte: minYearMonth,
            },
          },
          {
            year_month: {
              $lte: maxYearMonth,
            },
          },
        ] as any,
      },
    },
    {
      enabled: isRecordQueryEnabled,
    }
  );

  const isPropertyFilterEnabled = !!measurement_type_filter;

  // This hook enhances record values with calculator_result configuration to have estimation_method_entity_id:
  const { recordOrRecords: items, isLoading: isEnhancing } =
    useEnhanceRecordResources(recordQuery?.data?.data ?? [], {
      enabled: isPropertyFilterEnabled,
    });

  const unfilteredRecordValueTypes: string[] = Array.from(
    new Set(
      (items as RecordType[])?.flatMap(({ values }: RecordType) =>
        values.map(
          ({ measurement_type }: { measurement_type: string }) =>
            measurement_type
        )
      )
    )
  );

  const isLoading =
    (isRecordQueryEnabled && recordQuery.isLoading) ||
    (isPropertyFilterEnabled && isEnhancing) ||
    isMeasurementTypesLoading;

  const recordValueTypes = isPropertyFilterEnabled
    ? [measurement_type_filter]
    : unfilteredRecordValueTypes;

  const actionRow = [
    <Button
      key="apply-automation"
      icon="play-circle"
      isLoading={applySeededAutomationAsync.isLoading}
      onClick={() => setShowApplyWarning(true)}
    >
      Apply Record Automation
    </Button>,
    <PermissionAwareExportButton
      key="export-records"
      onClick={handleExport}
      isExporting={isExporting}
    />,
  ];

  const form = useForm({});

  useEffect(() => {
    if (!showApplyWarning) {
      form.reset();
    }
  }, [showApplyWarning]);

  const handleSubmit = form.handleSubmit((values) => {
    const { overwrite_all_values } = values;
    setShowApplyWarning(false);
    applySeededAutomationAsync.mutate({
      filters: {
        asset_id,
        ...(reporting_group_id
          ? { reporting_group_id: { $exact: reporting_group_id } }
          : {}),
        ...(measurement_type_filter
          ? { measurement_type: { $exact: measurement_type_filter } }
          : {}),
        year_month: getDiscreteYearMonths(minYearMonth, maxYearMonth),
      },
      overwriteAllValues: overwrite_all_values,
    });
  });

  const propertySpecificHeaders = useRecordMeasurementTypeSpecificTableHeader(
    measurement_type_filter
  );

  const { openDrawer, drawerState, navigationProps, onClose, recordValue } =
    useRecordValueDrawer({
      items: items as RecordType[],
      itemToAssetIdFunction: () => asset_id,
    });

  const headers: Array<HeaderType<RecordType>> = [
    {
      key: "year_month",
      label: "Time Period",
      isSortable: true,
      renderComponent: ({ item: { year_month, id } }: { item: RecordType }) =>
        year_month ? (
          <RoutingLink to={linkToRecordDetail(type, asset_id, id)}>
            {monthFormatter(yearMonthParser(year_month))}
          </RoutingLink>
        ) : (
          "-"
        ),
    },
    {
      key: "status",
      label: "Record Status",
      isSortable: false,
      renderComponent: ({ item }) => {
        const { values, id } = item;
        return (
          <InlineRecordValueStatusPicker
            recordId={id}
            values={values}
          />
        );
      },
    },
    ...recordValueTypes.map((key) => ({
      key,
      label: getTypeName(key),
      alignment: "right" as any,
      renderComponent: ({
        item: { values, year_month, id },
      }: {
        item: RecordType;
      }) => {
        const findValue = values.find(
          ({ measurement_type }: { measurement_type: string }) =>
            measurement_type === key
        );

        return (
          <RecordValueCell
            value={findValue}
            onClick={() => openDrawer(asset_id, id, year_month, key)}
          />
        );
      },
    })),
    ...propertySpecificHeaders,
  ];

  const getItemActions = ({ item }: { item: RecordType }): ItemAction[] => {
    return [
      {
        label: "Apply Automation",
        buttonProps: {
          onClick: () =>
            applyAutomation.mutate({
              filters: { id: item.id },
              overwriteAllValues: false,
            }),
        },
      },
    ];
  };

  return (
    <>
      <DataTablePanel
        panelProps={{ title: "Records", actionRow }}
        storageKey={tableConfigStorageKey}
        filterComponent={
          <RecordsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            selectableProperties={unfilteredRecordValueTypes}
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          headers,
          items: items as RecordType[],
          isLoading,
          selected: selectedRecords,
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            <BulkRecordStatusChangeButton
              key="status-change"
              filter={{ id: Object.keys(selectedRecords) }}
              recordsCount={Object.keys(selectedRecords).length}
              buttonProps={buttonProps}
            />,
          ],
          onSelectionChange: setSelectedRecords,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: recordQuery.data?.total_entries,
          },
        }}
      />
      <RecordValueDrawer
        recordValue={recordValue}
        state={drawerState}
        onClose={onClose}
        assetType={type}
        applyAutomation={(id) =>
          applyAutomation.mutate({ filters: { id }, overwriteAllValues: false })
        }
        navgivationProps={navigationProps}
      />
      <Dialog
        key="apply-automation"
        title="Apply Record Automation?"
        isOpen={showApplyWarning}
        onClose={() => setShowApplyWarning(false)}
        actionRow={[
          <Button
            key="apply-automation-action"
            isLoading={applySeededAutomationAsync.isLoading}
            variant="primary"
            onClick={handleSubmit}
          >
            Apply Automation
          </Button>,
        ]}
      >
        <p>You have selected to apply record automation.</p>

        <Accordion>
          <Accordion.AccordionPanel
            title="Advanced Configuration"
            dataKey="advanced-configuration"
          >
            <p style={{ width: 484 }}>
              Applying record automation will populate record values based off
              predefined automations to all applicable empty record value
              fields. Manually modified values will not be overwritten unless
              otherwise specified.
            </p>
            <Form {...form}>
              <CheckboxInput
                name="overwrite_all_values"
                label="Overwrite manual values with automation?"
                isLabelShown={false}
                isFluid
              />
            </Form>
          </Accordion.AccordionPanel>
        </Accordion>
      </Dialog>
    </>
  );
};
